import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  User, 
  Building2, 
  Mail, 
  Phone, 
  ArrowLeft, 
  Check,
  AlertCircle,
  Users
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import PhoneInput, { isValidE164 } from '../ui/PhoneInput';
import { useLanguage } from '../../contexts/LanguageContext';

const StaffSignUp = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();

  const [staffForm, setStaffForm] = useState({
    name: '',
    email: '',
    phone: '',
    brandId: '',
  });

  const [step, setStep] = useState(1);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const handleBrandIdVerification = async () => {
    const brandId = (staffForm.brandId || '').trim().toUpperCase();
    if (!brandId) {
      toast({ title: t('staff.t.brandRequiredTitle'), description: t('staff.t.brandRequiredDesc'), variant: "destructive" });
      return;
    }
    setVerifying(true);
    try {
      // PUBLIC verification endpoint (no auth). Only a real, resolvable Brand ID may proceed —
      // no false "accept" on auth/other errors (that previously showed "Verified" then 404'd on submit).
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/staff/verify-brand/${encodeURIComponent(brandId)}`
      );

      if (res.ok) {
        const data = await res.json();
        setBusinessInfo({
          name: data.business_name || brandId,
          sector: data.sector || '',
          subSector: data.sub_sector || '',
        });
        setStaffForm(prev => ({ ...prev, brandId }));
        setStep(2);
        toast({ title: t('staff.t.verifiedTitle'), description: t('staff.t.verifiedDesc', { business: data.business_name || brandId }) });
        return;
      }

      if (res.status === 404) {
        toast({ title: t('staff.t.invalidBrandTitle'), description: t('staff.t.invalidBrandDesc'), variant: "destructive" });
        return;
      }

      const err = await res.json().catch(() => ({}));
      toast({ title: t('staff.t.couldNotVerifyTitle'), description: err.detail || t('staff.t.couldNotVerifyDesc'), variant: "destructive" });
    } catch (e) {
      toast({ title: t('staff.t.connErrorTitle'), description: t('staff.t.connVerifyDesc'), variant: "destructive" });
    } finally {
      setVerifying(false);
    }
  };

  const handleStaffSignUp = async (e) => {
    e.preventDefault();

    if (!staffForm.name || !staffForm.email || !staffForm.phone) {
      toast({ title: t('common.error'), description: t('signup.t.fillFields'), variant: "destructive" });
      return;
    }
    if (!isValidE164(staffForm.phone)) {
      toast({ title: t('staff.t.invalidPhoneTitle'), description: t('staff.t.invalidPhoneDesc'), variant: "destructive" });
      return;
    }

    setVerifying(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/staff`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            brand_id: (staffForm.brandId || '').trim().toUpperCase(),
            name: staffForm.name,
            email: staffForm.email,
            phone: staffForm.phone,   // already E.164 from PhoneInput
            role: 'operator',          // server forces Operator regardless; sent for clarity
          }),
        }
      );

      if (res.status === 409) {
        toast({ title: t('staff.t.phoneRegisteredTitle'), description: t('staff.t.phoneRegisteredDesc'), variant: "destructive" });
        return;
      }
      if (res.status === 410) {
        toast({ title: t('staff.t.inviteExpiredTitle'), description: t('staff.t.inviteExpiredDesc'), variant: "destructive" });
        return;
      }
      if (res.status === 403) {
        const err = await res.json().catch(() => ({}));
        toast({ title: t('staff.t.regBlockedTitle'), description: err.detail || t('staff.t.regBlockedDesc'), variant: "destructive" });
        return;
      }
      if (res.status === 404) {
        toast({ title: t('staff.t.brandNotFoundTitle'), description: t('staff.t.brandNotFoundDesc'), variant: "destructive" });
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast({ title: t('staff.t.regFailedTitle'), description: err.detail || t('staff.t.regFailedDesc'), variant: "destructive" });
        return;
      }

      setStep(3);
      toast({ title: t('staff.t.accountCreatedTitle'), description: t('staff.t.accountCreatedDesc') });
    } catch {
      toast({ title: t('staff.t.connErrorTitle'), description: t('staff.t.connDesc'), variant: "destructive" });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/login')}
            className="absolute left-6 top-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('signup.backToLogin')}
          </Button>
          <div className="flex items-center justify-center mb-4">
            <Phone className="h-10 w-10 text-purple-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Servisfy</h1>
          </div>
          <p className="text-xl text-gray-600">{t('staff.registration')}</p>
        </div>

        <Card className="backdrop-blur-sm bg-white/90 border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl text-center flex items-center justify-center space-x-2">
              <Users className="h-6 w-6" />
              <span>{t('staff.joinTeam')}</span>
            </CardTitle>
            <CardDescription className="text-center">
              {t('staff.joinDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {step === 1 && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">{t('staff.processTitle')}</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• {t('staff.process1')}</li>
                    <li>• {t('staff.process2')}</li>
                    <li>• {t('staff.process3')}</li>
                    <li>• {t('staff.process4')}</li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="brandId">{t('staff.brandIdLabel')}</Label>
                    <div className="flex space-x-2">
                      <Input
                        id="brandId"
                        placeholder={t('staff.brandIdPh')}
                        value={staffForm.brandId}
                        onChange={(e) => setStaffForm(prev => ({ ...prev, brandId: e.target.value.toUpperCase().replace(/\s/g, '') }))}
                        maxLength={16}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        onClick={handleBrandIdVerification}
                        disabled={(staffForm.brandId || '').trim().length < 4 || verifying}
                      >
                        {verifying ? t('auth.verifying') : t('staff.verify')}
                      </Button>
                    </div>
                    <p className="text-sm text-gray-600">
                      {t('staff.getFromAdmin')}
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-yellow-600" />
                    <h4 className="font-medium text-yellow-900">{t('staff.needBrandId')}</h4>
                  </div>
                  <p className="text-sm text-yellow-800 mt-1">
                    {t('staff.contactAdminPre')}
                    <Button variant="link" className="p-0 h-auto text-yellow-800 underline" onClick={() => navigate('/signup')}>
                      {t('staff.createBusinessLink')}
                    </Button>
                    {t('staff.contactAdminPost')}
                  </p>
                </div>
              </div>
            )}

            {step === 2 && businessInfo && (
              <form onSubmit={handleStaffSignUp} className="space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <h4 className="font-medium text-green-900">{t('staff.brandIdVerified')}</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Building2 className="h-4 w-4 text-green-600" />
                      <span className="font-medium">{businessInfo.name}</span>
                    </div>
                    <div className="text-sm text-green-700">
                      <div>{t('staff.sectorLabel')} {businessInfo.sector}</div>
                      <div>{t('staff.subSectorLabel')} {businessInfo.subSector}</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium">{t('staff.personalInfo')}</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="staffName">{t('signup.fullName')}</Label>
                      <Input
                        id="staffName"
                        placeholder={t('signup.fullNamePh')}
                        value={staffForm.name}
                        onChange={(e) => setStaffForm(prev => ({ ...prev, name: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="staffEmail">{t('staff.emailLabel')}</Label>
                      <Input
                        id="staffEmail"
                        type="email"
                        placeholder={t('staff.emailPh')}
                        value={staffForm.email}
                        onChange={(e) => setStaffForm(prev => ({ ...prev, email: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium">{t('staff.contact')}</h4>
                  <div className="space-y-2">
                    <Label htmlFor="staffPhone">{t('signup.phoneNumber')}</Label>
                    <PhoneInput
                      id="staffPhone"
                      value={staffForm.phone}
                      onChange={(e164) => setStaffForm(prev => ({ ...prev, phone: e164 }))}
                    />
                    <p className="text-xs text-gray-500">{t('staff.phoneHelper')}</p>
                  </div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-medium text-purple-900 mb-2">{t('staff.defaultRole')}</h4>
                  <div className="flex items-center space-x-2">
                    <Badge className="bg-blue-100 text-blue-800">{t('role.operator')}</Badge>
                    <span className="text-sm text-purple-800">
                      {t('staff.roleInfo')}
                    </span>
                  </div>
                </div>

                <Button type="submit" disabled={verifying} className="w-full h-12 bg-gradient-to-r from-green-500 to-purple-600 hover:from-green-600 hover:to-purple-700">
                  {verifying ? t('staff.creating') : t('staff.createAccount')}
                </Button>
              </form>
            )}

            {step === 3 && (
              <div className="text-center space-y-6">
                <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">{t('staff.accountCreated')}</h3>
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-green-900 mb-2">{t('staff.regDetails')}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('staff.businessLabel')}</span>
                        <span className="font-medium">{businessInfo?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('staff.brandIdColon')}</span>
                        <span className="font-medium">{staffForm.brandId}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('staff.initialRole')}</span>
                        <Badge className="bg-blue-100 text-blue-800">{t('role.operator')}</Badge>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">
                    {t('staff.activeLogin')}
                  </p>
                  <Button
                    onClick={() => navigate('/login')}
                    className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
                  >
                    {t('signup.goToLogin')}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StaffSignUp;